import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import { ChangeEvent, FormEvent, useState } from 'react';
import React from 'react';
import { useNavigate } from 'react-router';

import { Button, Text } from '@bai/components';
import {
  AngularServiceNames,
  getAngularService,
} from '@bai/features/angular-integration';
import { logout } from '@bai/features/user';
import { Auth } from '@bai/utils/types';

import {
  handleLoginFeatureSubmit,
  LoginFeatureResponse,
  loginFeatureValidator,
  ValidationType,
} from './loginFeatureSubmitHandler';
import { ValidationMessageType } from './loginFeatureTypes';
import { NewPassword } from './NewPassword';

interface TemporaryProps {
  primaryColor?: string;
  logo?: string;
  hasCustomLogo: boolean;
  handleCancel: () => void;
  handleSubmit: (
    event: FormEvent,
    endpoint: string,
    password: string | null,
    username: string | null,
  ) => Promise<LoginFeatureResponse | string>;
  username: string;
  portalCode?: string;
}

export function TemporaryPassword({
  primaryColor,
  logo,
  hasCustomLogo,
  handleCancel,
  handleSubmit,
  username,
  portalCode,
}: TemporaryProps) {
  const endpoint = 'api/account/login';

  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [password, setPassword] = useState('');

  const [validationResponse, setValidationResponse] = useState({
    password: 'valid',
  });

  let passwordValid =
    loginFeatureValidator(
      ValidationType.DEFAULTS_TO_EMPTY,
      null,
      password,
      null,
    ) && validationResponse.password === 'valid';

  const handleValidation = () => {
    passwordValid = loginFeatureValidator(
      ValidationType.IS_REQUIRED,
      null,
      password,
      null,
    );

    if (!passwordValid) {
      setValidationResponse({
        ...validationResponse,
        password: 'invalid',
      });
    } else {
      setValidationResponse({ ...validationResponse, password: 'valid' });
    }
  };

  const handlePasswordChange = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setPassword(e.target.value);

    handleValidation();
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleCancelReturnToLogin = async () => {
    // must log user out entering valid temp password authenticates a user
    const loginView = '/';

    await logout(portalCode ?? '');
    void navigate(
      !portalCode ? loginView : `${loginView}?portal=${portalCode}`,
    );
  };

  const newPasswordProps = {
    primaryColor,
    logo,
    handleCancel: handleCancelReturnToLogin,
    handleSubmit: handleLoginFeatureSubmit,
    username,
    portalCode,
    hasCustomLogo,
  };

  const handleSubmitResponse = async (event: FormEvent) => {
    event.preventDefault();
    handleValidation();

    if (username === '' || password === '') {
      return;
    }
    const tempPassResp: LoginFeatureResponse | number = (await handleSubmit(
      event,
      endpoint,
      password,
      username,
    )) as LoginFeatureResponse | number;

    if (typeof tempPassResp === 'number') {
      if (tempPassResp === 401) {
        // TODO: display or handle error?
      }
    } else if (
      typeof tempPassResp === 'object' &&
      tempPassResp.StatusCode === 200
    ) {
      const loginResult = tempPassResp.data as Auth;
      const userService = getAngularService(AngularServiceNames.USER_SERVICE);
      userService.updateFromReact?.(loginResult);
      setShowNewPassword(true);
    }
  };

  const tempPasswordMessageLine1 = `Password reset information has been sent to the email address associated with ${username}.`;
  const tempPasswordMessageLine2 =
    "If you did not receive an email please contact your organization's Training Administrator.";

  return (
    <Grid
      sx={{
        justifyContent: 'center',
        fontFamily: 'Calibri',
        margin: '0 0',
      }}
    >
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: '25px',
        }}
      >
        {!showNewPassword ? (
          <form
            onSubmit={(event: React.FormEvent<HTMLFormElement>) =>
              handleSubmitResponse(event)
            }
          >
            <Grid sx={{ margin: '0 0' }}>
              <Grid
                container
                sx={{
                  justifyContent: 'center',
                  margin: '0 auto',
                  width: hasCustomLogo ? '40%' : '60%',
                }}
              >
                <img
                  className="themed_logo"
                  src={logo}
                  alt="logo"
                  data-cy="logo"
                />
              </Grid>
              <Grid>
                <h1>Enter Temporary Password</h1>

                <div style={{ margin: '2em 0' }}>
                  <Text
                    sx={{
                      fontFamily: 'Calibri, sans-serif !important',
                      fontSize: '1.2em',
                      margin: '1em 0',
                    }}
                  >
                    {tempPasswordMessageLine1}
                  </Text>

                  <Text
                    sx={{
                      fontFamily: 'Calibri, sans-serif !important',
                      fontSize: '1.2em',
                      margin: '1em 0',
                    }}
                  >
                    {tempPasswordMessageLine2}
                  </Text>
                </div>
                <TextField
                  data-cy="username"
                  label="Username"
                  hidden={true as const}
                  value={username}
                />
                <TextField
                  data-cy="temporary-password"
                  label="Password"
                  variant="outlined"
                  type="text"
                  sx={{
                    fontFamily: 'Calibri, sans-serif !important',
                    width: '100%',
                    '& input': {
                      WebkitTextSecurity: showPassword ? 'none' : 'disc',
                      fontFamily: showPassword ? 'inherit' : 'caption',
                    },
                  }}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handlePasswordChange(event)
                  }
                  onBlur={handleValidation}
                  onKeyUp={handleValidation}
                  error={!passwordValid}
                  helperText={
                    !passwordValid
                      ? `Password ${ValidationMessageType.IS_REQUIRED}`
                      : ''
                  }
                  slotProps={{
                    input: {
                      inputProps: {
                        role: 'textbox',
                        'aria-label': 'Password',
                      },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            data-cy="password-visibility"
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Grid>
              <Button
                data-cy="temporary-password-button"
                type="submit"
                specificColorToUse={primaryColor}
              >
                Submit
              </Button>
              <Grid container>
                <Grid style={{ padding: '1em 0 0 0' }}>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="as-link"
                    data-cy="cancel-temporary-password"
                  >
                    Cancel
                  </button>
                </Grid>
              </Grid>
            </Grid>
          </form>
        ) : (
          <NewPassword {...newPasswordProps} />
        )}
      </Box>
    </Grid>
  );
}
